/**
 * StandDurumuKutulari.jsx — #489: Stand Durumu'nun dört durumu (İnşaat İzni / Borç / Firma İSG /
 * Denetim) yan yana kompakt kutular olarak: renkli ikon karesi + etiket + rozet. Ayrıntıları ve
 * eylem düğmesi varsayılan kapalı; bir kutunun "Detaylar" bağlantısı satırın ALTINDAKİ tek ortak
 * paneli o kutunun ayrıntısıyla açar (aynı anda tek kutu açık, açık olana tekrar basınca kapanır).
 * Tek panel seçildi çünkü dörtte bir genişlikteki bir kutu Denetim akordeonunu ve Evrak rozet
 * ızgarasını taşıyamaz, kutular da hizasız uzamaz.
 *
 * Sunum bileşeni: veri/mantık sayfada kalır. Her kutu `{ key, icon, label, value, details,
 * actionLabel, onAction }`; `value` true / false / null (tamam / sorunlu / veri yok), `details`
 * paneldeki içerik (yoksa kutuda "Detaylar" bağlantısı çıkmaz), `actionLabel` + `onAction` ayrıntının
 * altındaki düğme. Personel tarafındaki "Durum & Borç" sekmesi (#73) aynı satırı kendi içerikleriyle
 * kullanabilsin diye Firma sayfasından ayrı bir dosyada.
 */
import React, { useId, useState } from "react";
import { Row, Col, Card, CardBody, Collapse, Button } from "reactstrap";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";

// Diğer talep/borç sayfalarındaki (TalepQuantityCard, CommitmentTile, BorcDurumuPanel) aynı "kutu"
// dili: renkli ikon karesi + etiket + rozet. Yeşil tamam, kırmızı sorunlu, gri veri yok.
const DURUM_KUTU_RENK = { true: "#0ab39c", false: "#f06548" };
const DURUM_KUTU_RENK_YOK = "#878a99";

const durumRengi = (value) => DURUM_KUTU_RENK[value] ?? DURUM_KUTU_RENK_YOK;

// Rozet ikon karesiyle aynı rengi taşır (satır içi): bu temada bg-success gri, bg-secondary yeşil
// çiziliyor, sınıfla verilen renk ikonla uyuşmuyordu (Tamam gri, Veri Yok yeşil).
export function DurumRozeti({ value }) {
  const { t } = useTranslation("portal");
  const metin = value === null || value === undefined ? t("standGenel.veriYok") : value ? t("common:tamam") : t("standGenel.sorunlu");
  // reactstrap's Badge always adds a bg-* class (bg-secondary by default) whose !important beats an
  // inline colour, so the badge is a plain .badge element.
  return <span className="badge" style={{ background: durumRengi(value) }}>{metin}</span>;
}

function IkonKaresi({ icon: Icon, value, boyut = 48, ikonBoyutu = 22 }) {
  return (
    <div
      className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{ width: boyut, height: boyut, background: durumRengi(value) }}
    >
      <Icon size={ikonBoyutu} color="#fff" />
    </div>
  );
}

export default function StandDurumuKutulari({ kutular }) {
  const { t } = useTranslation("portal");
  const panelId = useId();
  // Açık kutu ve panelde gösterilen kutu ayrı tutulur: kapanırken Collapse animasyonu boyunca
  // içerik kaybolmasın diye son açılan kutu kapanınca da panelde kalır.
  const [acikKey, setAcikKey] = useState(null);
  const [gosterilenKey, setGosterilenKey] = useState(null);

  const toggle = (key) => {
    if (acikKey === key) {
      setAcikKey(null);
    } else {
      setAcikKey(key);
      setGosterilenKey(key);
    }
  };

  const gosterilen = kutular.find((k) => k.key === gosterilenKey);

  return (
    <div>
      <Row className="g-3">
        {kutular.map((k) => {
          const acik = acikKey === k.key;
          return (
            <Col xs={6} lg={3} key={k.key}>
              <Card className={`h-100 mb-0 text-center ${acik ? "border border-primary" : ""}`}>
                <CardBody className="d-flex flex-column align-items-center">
                  <IkonKaresi icon={k.icon} value={k.value} />
                  <div className="fw-semibold small mt-2 mb-2" style={{ minHeight: "2.6em" }}>{k.label}</div>
                  <DurumRozeti value={k.value} />
                  {k.details && (
                    <Button
                      color="link"
                      size="sm"
                      className="mt-auto pt-2 pb-0 text-decoration-none"
                      aria-expanded={acik}
                      aria-controls={panelId}
                      onClick={() => toggle(k.key)}
                    >
                      {acik ? t("standGenel.detaylariGizle") : t("standGenel.detaylar")}
                      {acik ? <ChevronUp size={14} className="ms-1" /> : <ChevronDown size={14} className="ms-1" />}
                    </Button>
                  )}
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Collapse isOpen={acikKey !== null} id={panelId}>
        {gosterilen && (
          <Card className="mt-3 mb-0">
            <CardBody>
              <div className="d-flex align-items-center gap-2 mb-2">
                <IkonKaresi icon={gosterilen.icon} value={gosterilen.value} boyut={28} ikonBoyutu={14} />
                <h6 className="mb-0 fw-semibold">{gosterilen.label}</h6>
              </div>
              {gosterilen.details}
              {gosterilen.onAction && (
                <div className="mt-3">
                  <Button color="info" outline size="sm" onClick={gosterilen.onAction}>{gosterilen.actionLabel}</Button>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </Collapse>
    </div>
  );
}
